import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faClipboardCheck } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { CheckCircle2, Loader2 as Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MultiUserCombobox } from '@/components/ui/MultiUserCombobox';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { useUsersList } from '@/contexts/UsersListProvider';
import { useTheme } from '@/contexts/ThemeContext';
import { getDocument, saveDocument } from '@/services/documentsService';
import { useTenancy } from '../../../contexts/TenancyContext';
import { getAuthUID } from '@/lib/authState';
import HelpPopoverHover, { HelpPopoverHoverProps } from '@/components/HelpPopoverHover';

interface ReviewDocDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: {
    title?: string;
    description?: string;
    date?: string;
    id?: string;

    nextReviewDate?: string;
    lastReviewDate?: string;
    fileName?: string;
  };
  onDataChanged?: () => void;
  helpData: HelpPopoverHoverProps;
}

interface Signer {
  label: string;
  value: string;
}

const mockSigners: Signer[] = [
  { label: 'Alice Smith', value: 'alice@company.com' },
  { label: 'Bob Jones', value: 'bob@company.com' },
  { label: 'External Signer', value: 'external@email.com' },
];

function ReviewedByCombobox({ reviewedBy, setReviewedBy }: { reviewedBy: string, setReviewedBy: (v: string) => void }) {
  const { users, loading } = useUsersList();
  return (
    <SingleUserCombobox
      selected={reviewedBy}
      setSelected={setReviewedBy}
      placeholder="Select reviewer..."
      className="w-full"
    />
  );
}

function isValidDate(date: any): date is Date {
  if (!date) return false;
  const d = (date instanceof Date) ? date : new Date(date);
  return d instanceof Date && !isNaN(d.valueOf());
}

function cleanTitle(title?: string) {
  return title?.replace(/^Review Document:\s*/i, '') || '';
}

const ReviewDocDialog: React.FC<ReviewDocDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged, helpData }) => {
  // --- PATCH: Track selected file for upload (not uploaded until review complete) ---
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [replaceLoading, setReplaceLoading] = React.useState(false);
  const { isDark } = useTheme();
  const { currentUser } = useTenancy();
  const [reviewedBy, setReviewedBy] = React.useState("");
  const [signatureRequired, setSignatureRequired] = React.useState('no');
  const [signers, setSigners] = React.useState<string[]>([]);
  const [reviewStatus, setReviewStatus] = React.useState('approved');
  const [reviewNotes, setReviewNotes] = React.useState('');
  const [lastReviewDate, setLastReviewDate] = React.useState<Date | undefined>(
    new Date()
  );
  const [nextReviewDate, setNextReviewDate] = React.useState<Date | undefined>(
    new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
  );
  const [doc, setDoc] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  // Clear validation error as soon as all required fields are filled
  React.useEffect(() => {
    if (validationError && lastReviewDate && nextReviewDate && reviewedBy) {
      setValidationError(null);
    }
  }, [lastReviewDate, nextReviewDate, reviewedBy, validationError]);

  // Set reviewedBy to current user from users list, or fallback to first user, when dialog opens or users load
  const { users, loading: usersLoading } = useUsersList();
  React.useEffect(() => {
    if (!open) return;
    let reviewerValue = '';
    if (users && users.length > 0) {
      // Try to find the current user in the users list
      const match = users.find(u => (
        u.value === currentUser?.email ||
        u.value === currentUser?.displayName
      ));
      reviewerValue = match ? match.value : users[0].value;
    } else {
      reviewerValue = currentUser?.email || currentUser?.displayName || '';
    }
    setReviewedBy(reviewerValue);
    setSignatureRequired('no');
    setSigners([]);
    setReviewStatus('approved');
    setReviewNotes('');
    setLastReviewDate(new Date());
    setNextReviewDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
    setDoc(selectedEvent?.fileName ? { fileName: selectedEvent.fileName } : null);
    setError(null);
    setLoading(false);
    setSelectedFile(null);
    setValidationError(null);
  }, [open, selectedEvent, currentUser, users, usersLoading]);

  // Load document if selectedEvent has an id
  React.useEffect(() => {
    async function loadDoc() {
      if (!selectedEvent?.id) return;
      setLoading(true);
      setError(null);
      try {
        // Use getDocument to fetch from Firestore
        const loadedDoc = await getDocument(selectedEvent.id);
        if (!loadedDoc) throw new Error('Document not found');
        // Only fill in state from backend if selectedEvent is missing a value
        if (!selectedEvent?.fileName && loadedDoc.fileName) {
          setDoc(loadedDoc);
        }
        if (loadedDoc.reviewedBy) {
          setReviewedBy(loadedDoc.reviewedBy);
        }
        if (loadedDoc.reviewStatus) {
          setReviewStatus(loadedDoc.reviewStatus);
        }
        if (loadedDoc.reviewNotes) {
          setReviewNotes(loadedDoc.reviewNotes);
        }
        if (!selectedEvent?.lastReviewDate && loadedDoc.lastReviewDate) {
          setLastReviewDate(new Date(loadedDoc.lastReviewDate));
        }
        if (!selectedEvent?.nextReviewDate && loadedDoc.nextReviewDate) {
          setNextReviewDate(new Date(loadedDoc.nextReviewDate));
        }
        // You may want to add similar logic for prevReviewDate or other fields as needed
      } catch (err: any) {
        setError(err.message || 'Error loading document');
        setDoc(null);
      } finally {
        setLoading(false);
      }
    }
    loadDoc();
  }, [open, selectedEvent]);

  // Download handler using SupportingDocs.Document
  const handleDownload = async () => {
    if (!doc) return;
    try {
      await doc.download();
    } catch (e: any) {
      setError(e.message || 'Download failed');
    }
  };

  // Replace handler using documentsService (if file replace is supported)
  const handleReplace = async (file: File) => {
    if (!doc) return;
    try {
      // Save updated file using documentsService
      await saveDocument(doc, file);
      // Refetch the document after replace
      const updatedDoc = await getDocument(doc.docID);
      setDoc(updatedDoc);
      if (typeof onDataChanged === 'function') onDataChanged();
    } catch (e: any) {
      setError(e.message || 'Replace failed');
    }
  };

  // Utility to remove undefined fields
  function removeUndefined(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
  }

  // Complete Review handler
  const handleCompleteReview = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[ReviewDocDialog] Submit handler called', { lastReviewDate, nextReviewDate, reviewedBy });
    setValidationError(null);
    // Validation: required fields
    if (!lastReviewDate) {
      setValidationError('Review date is required.');
      return;
    }
    if (!nextReviewDate) {
      setValidationError('Next review date is required.');
      return;
    }
    if (!reviewedBy) {
      setValidationError('Reviewed by is required.');
      return;
    }
    if (!doc) return;
    setLoading(true);
    setError(null);
    try {
      // Gather all relevant fields
      const {
        classification,
        procRef,
        confluenceURL,
        fileName,
        docHash,
      } = doc;
      // Fetch original data to get all fields
      const originalDoc = await getDocument(doc.docID);
      if (!originalDoc) {
        throw new Error('Original document not found');
      }

      // Prepare review data
      const reviewData: any = {
        docID: doc.docID,
        companyID: window.companyID,
        comments: reviewNotes,
        lastReviewDate: lastReviewDate,
        nextReviewDate: nextReviewDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        reviewedBy,
        reviewStatus,
        signatureRequired,
        signers,
        classification: classification || originalDoc.classification || 'Business Confidential',
        procRefs: procRef || originalDoc.procRefs || [],
        procRef: procRef || originalDoc.procRef || [],
        uploadedBy: currentUser?.userID || getAuthUID(),
        confluenceURL,
        fileName,
        docHash,
        edit: true,
        // Add any other fields as needed
      };

      // Remove undefined fields before sending
      const cleanedReviewData = removeUndefined(reviewData);

      // Update doc instance fields
      Object.assign(doc, cleanedReviewData);

      // PATCH: Upload file if one is selected
      await saveDocument(doc, selectedFile);
      setSelectedFile(null);
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Error completing review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl! w-full max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card sm:p-0! shadow-2xl [&>button]:hidden p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col h-full max-h-[90vh] p-0">
          {/* Close button */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="absolute right-3 top-3 z-50 h-8 w-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {/* Header */}
          <div className="px-6 py-5 border-b border-border bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-emerald-500/10 to-emerald-500/30 mb-4">
              <FontAwesomeIcon icon={faClipboardCheck} className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Document Review</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              {selectedEvent?.title || selectedEvent?.fileName || doc?.fileName || 'Review document and update its status'}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto space-y-4">
            {validationError && (
              <div className="flex items-center text-red-600 text-sm mb-2 font-semibold">
                <svg className="h-5 w-5 mr-1 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg>
                {validationError}
              </div>
            )}
            <form onSubmit={handleCompleteReview}>
              <div className="space-y-4">
                {/* Name */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-3">
                    Name
                  </Label>
                  <Input
                    id="doc-name"
                    value={
                      selectedEvent?.fileName
                        ? selectedEvent.fileName
                        : cleanTitle(selectedEvent?.title) || ''
                    }
                    className="bg-background border-border"
                    readOnly
                    autoFocus={false}
                    data-force-isms-override
                  />
                </div>

                {/* Reviewed By */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-3">
                    Reviewed By
                  </Label>
                  <ReviewedByCombobox reviewedBy={reviewedBy} setReviewedBy={setReviewedBy} />
                </div>

              {/* Signature Required */}
              {/* <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="signature-required" className="text-right text-foreground whitespace-nowrap">
                Signature Required
              </Label>
              <div className="col-span-3 w-full">
                <Select value={signatureRequired} onValueChange={setSignatureRequired}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div> */}

              {/* Signers (if required) */}
              {/* {signatureRequired === 'yes' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="signers" className="text-right text-foreground">
                  Signers
                </Label>
                <div className="col-span-3 w-full">
                  <div className="w-full">
                    <MultiUserCombobox
                      users={mockSigners}
                      selected={signers}
                      setSelected={setSigners}
                      placeholder="Select signers..."
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )} */}

                {/* Review Date */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-3">
                    Date Reviewed
                  </Label>
                  <DatePicker
                    date={isValidDate(lastReviewDate) ? lastReviewDate : undefined}
                    setDate={setLastReviewDate}
                    fullWidth
                    enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
                  />
                </div>

                {/* Next Review Date */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-3">
                    Next Review Date
                  </Label>
                  <DatePicker
                    date={isValidDate(nextReviewDate) ? nextReviewDate : undefined}
                    setDate={setNextReviewDate}
                    fullWidth
                    enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
                  />
                </div>

                {/* Review Notes */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    Comments
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add any notes about this review
                  </p>
                  <Textarea
                    id="review-notes"
                    placeholder="Enter comments about this document review..."
                    className="bg-background border border-border min-h-[100px] focus:border-primary"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                </div>

                {/* Download/Replace Buttons */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-3">
                    Document Actions
                  </Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={handleDownload} className="flex-1">
                      Download
                    </Button>
                    {/* Replace Button: only selects file, does not upload */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex-1 ${selectedFile ? 'bg-green-100 border-green-400' : ''}`}
                      disabled={!!selectedFile}
                    >
                      {selectedFile ? (
                        <span className="flex items-center text-green-700">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          Selected
                        </span>
                      ) : 'Replace'}
                    </Button>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setSelectedFile(file);
                      setReplaceLoading(false);
                      setError(null);
                      e.target.value = '';
                    }}
                  />
                  {/* Show selected file name and clear option */}
                  {selectedFile && (
                    <div className="mt-2 flex items-center text-xs text-green-700" data-force-isms-override>
                      {selectedFile.name}
                      <Button size="icon" variant="ghost" className="ml-1 p-1 h-6 w-6" onClick={() => setSelectedFile(null)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/30 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-muted-foreground truncate">
                {selectedEvent?.id && `ID: ${selectedEvent.id}`}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCompleteReview}
                  disabled={loading}
                  className="bg-primary hover:bg-primary/80 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Complete Review
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDocDialog;
